export interface VentilationState {
  temperatures: {
    outsideAir: number; // getTemperatures(callback);
    supplyAir: number; // getTemperatures(callback);
    outgoingAir: number; // getTemperatures(callback);
    exhaustAir: number; // getTemperatures(callback);
    comfortValue: number; // getTemperatures(callback);
  };
  airFlows: {
    rotationsSupply: number; // getFanState(callback);
    rotationsOutgoing: number; // getFanState(callback);
    supplyAirPercentage: number; // getFanState(callback);
    outgoingAirPercentage: number; // getFanState(callback);
  };
  bypassPercentage: number; // getFlapState(callback);
  preheat: boolean; // getFlapState(callback);
  filterHours: number; // getOperatingHours(callback);
  supplyFanRunning: boolean; // getVentilationLevel(callback);
  currentLevel: 'away' | 'low' | 'middle' | 'high'; // getVentilationLevel(callback);
  // Optional (added with the energy control plane; older vent.js builds don't send them).
  operatingHours?: VentilationLocalState['hours']; // getOperatingHours(callback), lifetime hours
  levelPercents?: VentLevelPercents; // getVentilationLevel(callback), fan % behind each level
  control?: {
    lastCommandedLevel?: VentLevel; // last level set through vent.ts's local endpoint
    lastCommandedAt?: number; // epoch ms
    manualOverrideSince?: number; // epoch ms; set while the wall switch holds the level above the commanded one
  };
  receivedAt?: number; // epoch ms when the server received it (GET /ventilation/state only)
}

export interface SolarState {
  consumePowerWatts: number;
  batteryPowerWatts: number;
  feedInPowerWatts: number;
  batteryPercentage: number;
  solarProductionWatts: number;
  receivedAt?: number; // epoch ms when the server received it (GET /solar/state only)
}

export type VentLevel = 'away' | 'low' | 'middle' | 'high';
export const VENT_LEVELS: VentLevel[] = ['away', 'low', 'middle', 'high'];
// Supply / exhaust fan % configured for each level (0xCD); the 3-position switch picks low/middle/high.
export type VentLevelPercents = Record<VentLevel, { supply: number; exhaust: number }>;

// What vent.ts serves on its local endpoint (GET /state) for the energy controller.
export interface VentilationLocalState {
  timestamp: number; // epoch ms
  currentLevel: VentLevel;
  outsideAirC: number; // NB: measured after the preheater, so it plateaus around -2 °C
  supplyAirC: number;
  preheatActive: boolean;
  // Lifetime counters from getOperatingHours (hours).
  hours: {
    away: number;
    low: number;
    middle: number;
    high: number;
    frostProtection: number;
    preHeating: number;
    bypassOpen: number;
    filter: number;
  };
  // Fan % configured behind each level (getVentilationLevel); missing from older vent.js builds.
  levelPercents?: VentLevelPercents;
  // Last level set through the local endpoint, and when (epoch ms).
  lastCommandedLevel?: VentLevel;
  lastCommandedAt?: number;
  // Set (epoch ms, when it started) while the wall switch holds the level above the commanded one;
  // the controller must not command until the switch is back on 1.
  manualOverrideSince?: number;
}

// Apply state of the daily charge windows (Pi: planner.ts): written to the inverter, derived only (shadow mode),
// refused by the daily EEPROM write budget (retried), or the write failed (retried).
export type WindowsStatus = 'applied' | 'shadow' | 'eeprom-budget' | 'failed';

// Snapshot of the energy controller's current plan, pushed by the Pi after every replan
// (POST /energy/plan) and shown in the client's Energy tab.
export interface EnergyPlanSlot {
  start: number; // epoch ms, 15-minute slot start
  action: 'auto' | 'hold' | 'charge' | 'discharge' | 'export';
  batteryPowerW?: number; // for charge/discharge/export
  curtailExport: boolean;
  socStartPercent: number;
  socEndPercent: number;
  gridKwh: number; // + import, - export
  costCents: number;
  spot: number; // c/kWh excl VAT
  importPrice: number; // c/kWh all-in
  exportPrice: number; // c/kWh
  estimatedPrice: boolean;
  loadKwh: number;
  pvKwh: number;
  outdoorTempC?: number;
  ventLevel?: VentLevel;
}

export interface EnergyActualSlot {
  start: number;
  socEndPercent?: number;
  gridKwh?: number;
  pvKwh?: number;
  loadKwh?: number;
  outdoorTempC?: number; // measured (väljas sensor); Open-Meteo where the sensor had no data
  spot?: number; // c/kWh excl VAT (older controllers: absent)
  importPrice?: number;
  exportPrice?: number; // (older controllers: absent)
  action?: EnergyPlanSlot['action']; // what was actually commanded
  // Grid outage: the inverter ran off-grid on its backup output and the optimizer was paused for at least a minute
  // of the slot (a separate flag, so older clients keep working).
  gridOutage?: boolean;
  ventLevel?: VentLevel;
}

export interface EnergyPlanSnapshot {
  createdAt: number;
  mode: 'shadow' | 'windows' | 'live';
  ventilationMode: 'off' | 'shadow' | 'live';
  socPercent: number;
  socFloorPercent?: number; // the controller's SoC floor (older snapshots: absent)
  totalCostCents: number;
  selfUseCostCents: number;
  // Cost minus the value of the energy left in the battery at the horizon end (older snapshots: absent).
  // The fair saving is selfUseNetCostCents - netCostCents.
  netCostCents?: number;
  selfUseNetCostCents?: number;
  selfUseEndSocPercent?: number;
  slots: EnergyPlanSlot[]; // empty while the controller has no plan
  planCreatedAt?: number; // createdAt of the plan the slots come from (older controllers: absent)
  planError?: string; // the latest replan failed: the slots are from the older plan (or empty)
  actual: EnergyActualSlot[]; // recent past slots (e.g. last 24 h)
  chargeWindows?: {
    gridChargeEnabled: boolean;
    targetSocPercent: number;
    windows: { start: string; end: string }[]; // local HH:MM
    status?: WindowsStatus; // older controllers: absent
  };
  // resolvedAt: the condition is over (older controllers don't send it: every alert counts as open).
  alerts: { timestamp: number; kind: string; message: string; resolvedAt?: number }[];
  // Optional (observability; older controllers don't send them).
  yesterday?: EnergyDailySummaryLite;
  ventManualOverrideSince?: number; // epoch ms, while a wall-switch override holds automatic control off
  configVersion?: string; // hash of the controller's effective tunables
  airQuality?: EnergyAirQuality; // uHoo monitors (absent when the controller has no uHoo API key)
  power?: EnergyPowerSeries; // 5-minute mean powers of the actual period (older controllers: absent)
  batteryNominalKwh?: number; // the battery's nominal capacity, the base of the SoC %
  monthToDate?: EnergyMonthToDate; // real cost of the current month so far
}

// What the current local month has cost so far (Pi: monitoring/month_to_date.ts).
export interface EnergyMonthToDate {
  month: string; // YYYY-MM (Tallinn)
  costCents: number; // real cost incl VAT: grid energy + all per-kWh fees, export credited, monthly fees, NO wear
  savingVsSelfUseCents?: number; // Σ of the closed days' savings vs plain Self Use
  daysCovered: number; // days with a daily_summary row (+ today if it has data)
  daysInPeriod: number; // days elapsed in the month incl today
  completeness: number; // 0..1, slot coverage
}

// 5-minute mean powers (Pi: telemetry_5m), finer than the 15-minute actual slots so short loads (heat pump,
// sauna) show. Columnar to keep the snapshot small: value i belongs to the interval start + i × stepMs;
// null = no telemetry.
export interface EnergyPowerSeries {
  start: number; // epoch ms
  stepMs: number;
  loadW: (number | null)[];
  pvW: (number | null)[];
  gridW: (number | null)[]; // + import, - export
  batteryW: (number | null)[]; // + charge, - discharge
  socPercent: (number | null)[]; // at the end of the interval
}

// Air quality from the uHoo monitors (Pi: monitoring/air_quality.ts, table air_quality_15m).
export interface AirQualityReading {
  timestamp: number; // epoch ms of the sample (one per minute, ~1-2 min behind)
  co2?: number; // ppm
  temperatureC?: number;
  humidity?: number; // %
  pm25?: number; // µg/m³
  tvoc?: number; // ppb
}

export interface EnergyAirQualityRoom {
  name: string; // uHoo device name (Kabinet, Magamistuba)
  latest?: AirQualityReading;
  slots: { start: number; co2: number; co2Max?: number }[]; // last 24 h, 15-min slot means (ppm)
}

export interface EnergyAirQuality {
  thresholds: { good: number; high: number }; // ppm, what the daily summary counts hours above
  rooms: EnergyAirQualityRoom[];
}

// Headline numbers of the previous local day's summary (Pi: daily_summary).
export interface EnergyDailySummaryLite {
  date: string; // YYYY-MM-DD, Tallinn
  actualCostCents?: number; // grid + battery wear
  gridCostCents?: number; // grid only: what was actually paid (no battery wear)
  savingVsSelfUseCents?: number; // + = cheaper than plain Self Use on the same load / PV
  savingVsOldScheduleCents?: number; // + = cheaper than the old fixed night charge
  loadMaeKwh?: number; // load forecast mean absolute error per 15-min slot (decision lead)
  pvMaeKwh?: number;
  failsafeMinutes: number;
  completeness: number; // share of the day with telemetry, 0..1
}
